/**
 * Nuclear finding relevance — gates HALEU and reactor-specific findings
 * on project intent reactor design class (site-level).
 */
import type { ProjectIntent } from '../types/siteRag';
import {
  getReactorDesignClass,
  resolveReactorDesignFromIntent,
} from '../config/reactorDesignCatalog';

export type ReactorFuelClass = 'HALEU' | 'LEU' | 'mixed' | 'unknown';

const HALEU_KEYWORDS = /\bhaleu\b|high[- ]assay|5-20%\s*enrichment/i;

export function intentUsesHaleuPath(intent: ProjectIntent | undefined): boolean {
  if (!intent || intent.nuclearInterest === 'no' || intent.nuclearInterest === 'undecided') {
    return false;
  }
  const entry = resolveReactorDesignFromIntent(intent);
  if (entry.id !== 'undecided') {
    return entry.damSlotHints.haleuApplies || entry.fuelClass === 'HALEU';
  }
  // Legacy fallback: primary interest without class — HALEU may apply
  return intent.nuclearInterest === 'primary';
}

export function findingMentionsHaleu(text: string): boolean {
  return HALEU_KEYWORDS.test(text);
}

export function applyNuclearRelevanceGate(
  finding: {
    title: string;
    detail: string;
    intentContext?: string;
    validationWarning?: string;
    relevanceGate?: { appliesWhen: string; notApplicableWhen?: string };
    updateFieldPath?: string;
    updateFieldLabel?: string;
  },
  intent: ProjectIntent | undefined,
): typeof finding {
  const blob = `${finding.title} ${finding.detail}`;
  if (!findingMentionsHaleu(blob)) return finding;

  const design = resolveReactorDesignFromIntent(intent);
  const haleuPath = intentUsesHaleuPath(intent);

  if (haleuPath) {
    return {
      ...finding,
      relevanceGate: {
        appliesWhen: `Project intent selects ${design.label} (${design.fuelClass} fuel path).`,
        notApplicableWhen: undefined,
      },
      intentContext: finding.intentContext
        ?? `Relevant because the declared reactor design class (${design.label}) may require HALEU supply chain readiness.`,
      validationWarning: finding.validationWarning
        ?? 'Confirm reactor design class in Project Intent if this fuel-cycle constraint should not apply.',
      updateFieldPath: 'projectIntent.reactorDesignClass',
      updateFieldLabel: 'Reactor design class',
    };
  }

  return {
    ...finding,
    relevanceGate: {
      appliesWhen: 'Only if an HTGR-class or HALEU microreactor design is selected.',
      notApplicableWhen: `With ${design.label} (${design.fuelClass} fuel), HALEU may not apply. Update reactor design class if your path requires high-assay fuel.`,
    },
    intentContext: 'HALEU supply chain constraints apply only to high-assay fuel designs. With current project intent, treat this as conditional until reactor design class is confirmed.',
    validationWarning: design.id === 'undecided'
      ? 'Select a reactor design class in Project Intent to resolve whether HALEU applies.'
      : `${getReactorDesignClass(design.id).label} typically uses ${design.fuelClass} — HALEU may not affect your outcome.`,
    updateFieldPath: 'projectIntent.reactorDesignClass',
    updateFieldLabel: 'Reactor design class',
  };
}

export function haleuConflictWithIntent(intent: ProjectIntent | undefined): string | null {
  if (!intent?.reactorDesignClass || intent.reactorDesignClass === 'undecided') return null;
  const entry = getReactorDesignClass(intent.reactorDesignClass);
  if (entry.fuelClass === 'LEU' && intent.nuclearInterest !== 'no') {
    return null;
  }
  return null;
}

/** True when a HALEU-tagged finding conflicts with an LEU-class selection. */
export function hasHaleuIntentConflict(intent: ProjectIntent | undefined, findingText: string): boolean {
  if (!findingMentionsHaleu(findingText)) return false;
  const entry = resolveReactorDesignFromIntent(intent);
  if (entry.id === 'undecided') return false;
  return !intentUsesHaleuPath(intent);
}
